import { ArrowUpRight } from 'lucide-react'
import { formatShortDate, parseYmd } from '@loop3/dates'
import { Badge, safeHttpUrl } from '@loop3/ui'
import type { Trip } from '@/content/trips/manifest'
import { getTripStatus, STATUS_LABEL, type TripStatus } from '@/lib/trip-status'

/**
 * Format "2026-04-25" + "2026-05-02" as "Apr 25 – May 2, 2026".
 * If dates span years, includes year on both ends.
 */
function formatDateRange(startIso: string, endIso: string): string {
  const start = parseYmd(startIso)
  const end = parseYmd(endIso)
  const startYear = String(start.getFullYear())
  const endYear = String(end.getFullYear())
  const startLabel = formatShortDate(start)
  const endLabel = formatShortDate(end)
  if (startYear === endYear) {
    return `${startLabel} – ${endLabel}, ${endYear}`
  }
  return `${startLabel}, ${startYear} – ${endLabel}, ${endYear}`
}

const STATUS_STYLES: Record<TripStatus, string> = {
  active:
    'bg-[var(--l3-violet-soft)] text-[var(--l3-violet)] border-transparent',
  upcoming: 'bg-transparent text-foreground/70 border-border',
  past: 'bg-muted text-muted-foreground border-transparent',
}

export function TripCard({ trip }: { trip: Trip }) {
  const status = getTripStatus(trip)
  const safeUrl = safeHttpUrl(trip.artifactUrl)

  const body = (
    <>
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-sm font-medium leading-snug group-hover:text-[var(--l3-violet)] transition-colors">
          {trip.title}
        </h3>
        <ArrowUpRight
          aria-hidden="true"
          className="size-4 opacity-30 group-hover:opacity-100 group-hover:text-[var(--l3-violet)] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all shrink-0"
        />
      </div>
      <p className="text-xs opacity-70 font-mono uppercase tracking-wider">
        {formatDateRange(trip.startDate, trip.endDate)}
      </p>
      <p className="text-xs opacity-70 leading-relaxed line-clamp-2">
        {trip.tagline}
      </p>
      <div className="mt-auto pt-1">
        <Badge variant="outline" className={STATUS_STYLES[status]}>
          {STATUS_LABEL[status]}
        </Badge>
      </div>
    </>
  )

  if (!safeUrl) {
    return (
      <div className="group relative flex flex-col gap-3 p-5 border border-border rounded-lg opacity-60">
        {body}
      </div>
    )
  }

  return (
    <a
      href={safeUrl}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`${trip.title} — opens in new tab`}
      className="group relative flex flex-col gap-3 p-5 border border-border rounded-lg hover:border-[var(--l3-violet)] hover:bg-[var(--l3-violet-soft)] transition-colors"
    >
      {body}
    </a>
  )
}
